"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import type {
  SessionListItem,
  FolderResponse,
  SessionCreate,
  SessionUpdate,
  SessionResponse,
  FolderCreate,
  FolderUpdate,
} from "@/app/lib/types";
import {
  fetchSessions,
  fetchSession,
  createSession as apiCreateSession,
  updateSession as apiUpdateSession,
  fetchFolders,
  createFolder as apiCreateFolder,
  updateFolder as apiUpdateFolder,
  deleteFolder as apiDeleteFolder,
  batchSessions as apiBatchSessions,
} from "@/app/lib/api";
// deleteSession is routed through the outbox so network failures
// queue for retry instead of being silently swallowed.
import { outboxDeleteSession } from "@/app/lib/outbox";
import type { BatchRequest } from "@/app/lib/types";

interface UseSessionsReturn {
  sessions: SessionListItem[];
  folders: FolderResponse[];
  activeSessionId: string | null;
  isLoading: boolean;
  loadSessions: () => Promise<void>;
  loadFolders: () => Promise<void>;
  createSession: (data?: SessionCreate) => Promise<SessionResponse>;
  deleteSession: (id: string) => Promise<void>;
  updateSession: (id: string, data: SessionUpdate) => Promise<void>;
  setActiveSession: (id: string | null) => void;
  batchSessions: (data: BatchRequest) => Promise<void>;
  createFolder: (data: FolderCreate) => Promise<FolderResponse>;
  updateFolder: (id: string, data: FolderUpdate) => Promise<void>;
  deleteFolder: (id: string) => Promise<void>;
  getSession: (id: string) => Promise<SessionResponse>;
}

export function useSessions(): UseSessionsReturn {
  const [sessions, setSessions] = useState<SessionListItem[]>([]);
  const [folders, setFolders] = useState<FolderResponse[]>([]);
  const [activeSessionId, setActiveSession] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Tombstone set — session IDs the user has asked us to delete.
  // Reason: the 5s loadSessions() poll below repeatedly overwrites
  // local state with server's view. If a DELETE is in flight (or
  // outbox-queued for retry), the server still returns the session
  // and the poll re-adds it to the sidebar AFTER our optimistic
  // filter ran. That's been the recurring Stop+Discard failure mode.
  // Once an ID is tombstoned, EVERY loadSessions response filters it
  // out, so no poll can resurrect it. We never prune — the set is
  // bounded by session-deletes-per-app-lifetime, which is tiny.
  const tombstonedSessionIdsRef = useRef<Set<string>>(new Set());

  const loadSessions = useCallback(async () => {
    try {
      const resp = await fetchSessions();
      const tombstoned = tombstonedSessionIdsRef.current;
      const filtered = tombstoned.size > 0
        ? resp.sessions.filter((s) => !tombstoned.has(s.id))
        : resp.sessions;
      setSessions(filtered);
    } catch (err) {
      console.error("Failed to load sessions:", err);
    }
  }, []);

  const loadFolders = useCallback(async () => {
    try {
      const resp = await fetchFolders();
      setFolders(resp);
    } catch (err) {
      console.error("Failed to load folders:", err);
    }
  }, []);

  // Load on mount
  useEffect(() => {
    let mounted = true;
    async function load() {
      setIsLoading(true);
      await Promise.all([loadSessions(), loadFolders()]);
      if (mounted) setIsLoading(false);
    }
    load();
    return () => {
      mounted = false;
    };
  }, [loadSessions, loadFolders]);

  // Poll for updates every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadSessions();
      loadFolders();
    }, 5000);
    return () => clearInterval(interval);
  }, [loadSessions, loadFolders]);

  const createSession = useCallback(
    async (data?: SessionCreate) => {
      const session = await apiCreateSession(data || {});
      await loadSessions();
      setActiveSession(session.id);
      return session;
    },
    [loadSessions]
  );

  const deleteSessionHandler = useCallback(
    async (id: string) => {
      // 1. Tombstone IMMEDIATELY — before any await, before the 5s
      //    loadSessions poll has a chance to overwrite local state.
      //    From this moment on, no loadSessions response can put this
      //    id back into the sidebar even if the server's DELETE hasn't
      //    landed yet or is being retried by the outbox.
      tombstonedSessionIdsRef.current.add(id);
      // 2. Optimistic local removal so the sidebar clears in the same tick.
      if (activeSessionId === id) {
        setActiveSession(null);
      }
      setSessions((prev) => prev.filter((s) => s.id !== id));
      // 3. Server-side delete via outbox. outboxDeleteSession never
      //    throws — failures are silently queued for background retry.
      //    The tombstone keeps the UI consistent regardless of retry
      //    latency or polls firing mid-flight.
      await outboxDeleteSession(id);
    },
    [activeSessionId]
  );

  const updateSessionHandler = useCallback(
    async (id: string, data: SessionUpdate) => {
      await apiUpdateSession(id, data);
      await loadSessions();
    },
    [loadSessions]
  );

  const batchSessionsHandler = useCallback(
    async (data: BatchRequest) => {
      await apiBatchSessions(data);
      await loadSessions();
    },
    [loadSessions]
  );

  const createFolderHandler = useCallback(
    async (data: FolderCreate) => {
      const folder = await apiCreateFolder(data);
      await loadFolders();
      return folder;
    },
    [loadFolders]
  );

  const updateFolderHandler = useCallback(
    async (id: string, data: FolderUpdate) => {
      await apiUpdateFolder(id, data);
      await loadFolders();
    },
    [loadFolders]
  );

  const deleteFolderHandler = useCallback(
    async (id: string) => {
      // Collect child session IDs before deleting the folder — the server's
      // DELETE endpoint unfiles them, but we want to delete them entirely.
      const childIds = sessions
        .filter((s) => s.folder_id === id)
        .map((s) => s.id);

      await apiDeleteFolder(id);

      // Delete child sessions sequentially (they share the same encrypted
      // sessions file — parallel deletes would race). Outbox-wrapped so
      // transient failures queue for retry instead of being swallowed.
      for (const sid of childIds) {
        await outboxDeleteSession(sid);
      }

      await Promise.all([loadFolders(), loadSessions()]);
    },
    [loadFolders, loadSessions, sessions]
  );

  const getSession = useCallback(async (id: string) => {
    return fetchSession(id);
  }, []);

  return {
    sessions,
    folders,
    activeSessionId,
    isLoading,
    loadSessions,
    loadFolders,
    createSession,
    deleteSession: deleteSessionHandler,
    updateSession: updateSessionHandler,
    setActiveSession,
    batchSessions: batchSessionsHandler,
    createFolder: createFolderHandler,
    updateFolder: updateFolderHandler,
    deleteFolder: deleteFolderHandler,
    getSession,
  };
}
